import { createFileRoute, Link, useNavigate, retainSearchParams } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listAsns, listAsnSkus, createAsn, deleteAsn, importAsns } from "@/lib/receiving.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, Upload, Trash2 } from "lucide-react";
import { DataTable, type ColumnDef } from "@/components/ui/data-table";
import { FilterBar } from "@/components/ui/filter-bar";
import { PageHeader } from "@/components/ui/page-header";
import { ContextualTip } from "@/components/kb/contextual-tip";
import { ExportMenu } from "@/components/ui/export-menu";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import { ScanInput } from "@/components/ui/scan-input";
import { toast } from "sonner";
import { DateRangeFilter } from "@/components/filters/date-range-filter";
import { DateFieldSelect } from "@/components/filters/date-field-select";
import { SortDirectionToggle } from "@/components/filters/sort-direction-toggle";
import { dateRangeSearchSchema, dateRangeToBounds, retainDateRangeKeys } from "@/lib/date-range-search";

const asnsSearchSchema = dateRangeSearchSchema.extend({
  dateField: fallback(z.enum(["created_at", "expected_at"]).optional(), undefined),
});

export const Route = createFileRoute("/_authenticated/app/receiving/")({
  validateSearch: zodValidator(asnsSearchSchema),
  search: { middlewares: [retainSearchParams([...retainDateRangeKeys])] },
  component: AsnListPage,
});

type AsnRow = {
  id: string; reference: string; carrier: string | null;
  status: string; expected_at: string | null; customer_id: string; created_at: string;
};

function AsnListPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const urlSearch = Route.useSearch();
  const navigateSearch = useNavigate({ from: Route.fullPath });
  const dateField = (urlSearch.dateField ?? "created_at") as "created_at" | "expected_at";
  const sort = urlSearch.sort ?? "desc";
  const { fromIso, toIso } = useMemo(
    () => dateRangeToBounds({ start: urlSearch.start, end: urlSearch.end }),
    [urlSearch.start, urlSearch.end],
  );
  const { tenantId, facilityId, customerId, customers, facilities, tenants } = useScope();
  const listAsnsFn = useServerFn(listAsns);
  const listSkusFn = useServerFn(listAsnSkus);
  const createAsnFn = useServerFn(createAsn);
  const deleteAsnFn = useServerFn(deleteAsn);
  const importAsnsFn = useServerFn(importAsns);
  const tenantCustomers = useMemo(() => customers.filter((c) => c.tenant_id === tenantId), [customers, tenantId]);
  const [open, setOpen] = useState(false);
  const [reference, setReference] = useState(""); const [carrier, setCarrier] = useState("");
  const [formCustomer, setFormCustomer] = useState<string>(""); const [submitting, setSubmitting] = useState(false);
  const [draftLines, setDraftLines] = useState<{ sku_id: string; qty: string; lot: string }[]>([{ sku_id: "", qty: "", lot: "" }]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importCustomer, setImportCustomer] = useState<string>("");
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ created: number; skipped: number; errors: Array<{ reference: string; message: string }> } | null>(null);

  const { data: formSkus = [] } = useQuery({
    queryKey: ["skus-for-new-asn", tenantId, customerId ?? formCustomer],
    enabled: open && !!tenantId && !!(customerId ?? formCustomer),
    queryFn: () => listSkusFn({ data: {
      customerId: (customerId ?? formCustomer)!, tenantId: tenantId!, activeOnly: true,
    } }),
  });

  const { data, isLoading } = useQuery({
    queryKey: ["asns", tenantId, facilityId, customerId, fromIso, toIso, dateField, sort],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => listAsnsFn({ data: {
      tenantId: tenantId!, facilityId: facilityId!, customerId: customerId ?? null,
      fromIso, toIso, sort, dateField,
    } }) as Promise<AsnRow[]>,
  });
  const customerName = (id: string) => customers.find((c) => c.id === id)?.name ?? "—";

  const handleNoSetup = () => {
    if (!tenants.length) toast.error("Create a tenant first", { action: { label: "Go", onClick: () => navigate({ to: "/app/admin/tenants" }) } });
    else if (!facilities.filter((f) => f.tenant_id === tenantId).length) toast.error("Create a facility first", { action: { label: "Go", onClick: () => navigate({ to: "/app/admin/facilities" }) } });
    else toast.error("Select a facility in the scope bar above");
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cid = customerId ?? formCustomer;
    if (!tenantId || !facilityId || !cid) return toast.error("Pick tenant, facility, and customer");
    const lines = draftLines.map((l) => ({ sku_id: l.sku_id, qty: parseInt(l.qty, 10), lot: l.lot.trim() || null })).filter((l) => l.sku_id && l.qty > 0);
    setSubmitting(true);
    try {
      await runServerFn(
        () => createAsnFn({ data: {
          tenantId, facilityId, customerId: cid,
          reference: reference.trim(),
          carrier: carrier.trim() || null,
          lines: lines.map((l) => ({ skuId: l.sku_id, qty: l.qty, lot: l.lot })),
        } }),
        "Create ASN",
      );
      toast.success(lines.length > 0 ? `ASN created with ${lines.length} line${lines.length > 1 ? "s" : ""}` : "ASN created");
      setOpen(false); setReference(""); setCarrier(""); setFormCustomer("");
      setDraftLines([{ sku_id: "", qty: "", lot: "" }]);
      qc.invalidateQueries({ queryKey: ["asns"] });
    } catch { /* toast handled */ } finally { setSubmitting(false); }
  };

  const filtered = useMemo(() => {
    const rows = data ?? [];
    return rows.filter((a) => {
      if (statusFilter !== "all" && a.status !== statusFilter) return false;
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return a.reference.toLowerCase().includes(q)
        || (a.carrier ?? "").toLowerCase().includes(q)
        || customerName(a.customer_id).toLowerCase().includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, search, statusFilter, customers]);

  const onScan = (code: string) => {
    const rows = data ?? [];
    const hit = rows.find((a) => a.reference.toLowerCase() === code.trim().toLowerCase());
    if (hit) navigate({ to: "/app/receiving/$asnId", params: { asnId: hit.id } });
    else toast.error(`No ASN found for "${code}"`);
  };

  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const onBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!confirm(`Delete ${selectedIds.length} ASN(s)? Only ASNs without receipts will be deleted.`)) return;
    setBulkDeleting(true);
    let ok = 0, fail = 0;
    for (const id of selectedIds) {
      try { await deleteAsnFn({ data: { asnId: id } }); ok++; } catch { fail++; }
    }
    setBulkDeleting(false);
    setSelected({});
    qc.invalidateQueries({ queryKey: ["asns"] });
    if (ok > 0) toast.success(`Deleted ${ok} ASN${ok > 1 ? "s" : ""}`);
    if (fail > 0) toast.error(`${fail} could not be deleted (receipts exist or other error)`);
  };

  const parseImportCsv = (text: string): Array<{ reference: string; carrier: string | null; skuCode: string | null; qty: number | null; lot: string | null }> => {
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) return [];
    const header = lines[0].toLowerCase().split(",").map((c) => c.trim());
    const idx = (name: string) => header.indexOf(name);
    const refI = idx("reference"); const carI = idx("carrier");
    const skuI = idx("sku_code"); const qtyI = idx("qty"); const lotI = idx("lot");
    if (refI < 0) throw new Error("CSV must include a 'reference' column");
    const rows: Array<{ reference: string; carrier: string | null; skuCode: string | null; qty: number | null; lot: string | null }> = [];
    for (let i = 1; i < lines.length; i++) {
      const cells = lines[i].split(",").map((c) => c.trim());
      const ref = cells[refI];
      if (!ref) continue;
      const qtyRaw = qtyI >= 0 ? cells[qtyI] : "";
      const qtyN = qtyRaw ? parseInt(qtyRaw, 10) : null;
      rows.push({
        reference: ref,
        carrier: carI >= 0 && cells[carI] ? cells[carI] : null,
        skuCode: skuI >= 0 && cells[skuI] ? cells[skuI] : null,
        qty: qtyN && qtyN > 0 ? qtyN : null,
        lot: lotI >= 0 && cells[lotI] ? cells[lotI] : null,
      });
    }
    return rows;
  };

  const onImport = async () => {
    const cid = customerId ?? importCustomer;
    if (!tenantId || !facilityId) return toast.error("Select tenant and facility first");
    if (!cid) return toast.error("Select a customer for the import");
    let rows: ReturnType<typeof parseImportCsv> = [];
    try { rows = parseImportCsv(importText); } catch (e: any) { return toast.error(e?.message ?? "Invalid CSV"); }
    if (rows.length === 0) return toast.error("No rows to import");
    setImporting(true);
    setImportResult(null);
    try {
      const res = await runServerFn(
        () => importAsnsFn({ data: { tenantId, facilityId, customerId: cid, rows } }),
        "Import ASNs",
      );
      setImportResult(res);
      if (res.created > 0) toast.success(`Imported ${res.created} ASN${res.created > 1 ? "s" : ""}`);
      if (res.skipped > 0) toast.warning(`${res.skipped} reference(s) skipped`);
      qc.invalidateQueries({ queryKey: ["asns"] });
    } catch { /* toast surfaced */ } finally { setImporting(false); }
  };

  const cols: ColumnDef<AsnRow, unknown>[] = [
    {
      id: "select", header: () => {
        const ids = filtered.map((r) => r.id);
        const allChecked = ids.length > 0 && ids.every((id) => selected[id]);
        return (
          <input type="checkbox" checked={allChecked} onChange={(e) => {
            const next = { ...selected };
            for (const id of ids) next[id] = e.target.checked;
            setSelected(next);
          }} />
        );
      },
      enableSorting: false,
      cell: ({ row }) => (
        <input type="checkbox" checked={!!selected[row.original.id]} onClick={(e) => e.stopPropagation()} onChange={(e) => setSelected((s) => ({ ...s, [row.original.id]: e.target.checked }))} />
      ),
    },
    { id: "reference", header: "Reference", accessorKey: "reference", cell: ({ row }) => <span className="font-medium font-mono text-sm">{row.original.reference}</span> },
    { id: "customer", header: "Customer", accessorFn: (a) => customerName(a.customer_id), cell: ({ getValue }) => <span className="text-muted-foreground">{String(getValue() ?? "—")}</span> },
    { id: "carrier", header: "Carrier", accessorFn: (a) => a.carrier ?? "", cell: ({ row }) => <span className="text-muted-foreground">{row.original.carrier ?? "—"}</span> },
    { id: "status", header: "Status", accessorKey: "status", cell: ({ row }) => <StatusPill tone={statusTone(row.original.status)} dot>{row.original.status}</StatusPill> },
    { id: "created", header: "Created", accessorKey: "created_at", cell: ({ row }) => <span className="text-muted-foreground text-sm">{new Date(row.original.created_at).toLocaleDateString()}</span> },
    { id: "open", header: "", enableSorting: false, cell: ({ row }) => <Link to="/app/receiving/$asnId" params={{ asnId: row.original.id }} className="text-sm text-primary hover:underline">Open →</Link> },
  ];

  return (
    <>
      <PageHeader
        title="Receiving"
        description="Inbound shipments awaiting receipt and putaway."
        actions={
          <div className="flex flex-wrap items-center gap-2">
          {selectedIds.length > 0 && (
            <Button size="sm" variant="outline" onClick={onBulkDelete} disabled={bulkDeleting} className="text-destructive hover:text-destructive">
              {bulkDeleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete ({selectedIds.length})
            </Button>
          )}
          {tenantId ? (
            <ExportMenu
              entity="asns"
              tenantId={tenantId}
              facilityId={facilityId}
              customerId={customerId}
              getRows={() => filtered.map((a) => ({
                reference: a.reference,
                customer: customerName(a.customer_id),
                carrier: a.carrier ?? "",
                status: a.status,
                created_at: a.created_at,
              }))}
              columns={["reference","customer","carrier","status","created_at"]}
              filters={{ statusFilter, search }}
              disabled={filtered.length === 0}
            />
          ) : null}
          <Link to="/app/receiving/scorecard" className="text-sm text-muted-foreground hover:text-foreground whitespace-nowrap">Vendor scorecard</Link>
          <Dialog open={importOpen} onOpenChange={(v) => { if (v && !facilityId) { handleNoSetup(); return; } setImportOpen(v); if (!v) { setImportText(""); setImportResult(null); setImportCustomer(""); } }}>
            <DialogTrigger asChild><Button size="sm" variant="outline"><Upload className="h-4 w-4 mr-2" />Import</Button></DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>Import ASNs</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="text-xs text-muted-foreground">
                  Paste CSV with header row. Columns: <code className="font-mono">reference, carrier, sku_code, qty, lot</code>. Multiple rows sharing a reference become expected lines on one ASN. Only <code>reference</code> is required.
                </div>
                {!customerId && (
                  <div className="space-y-2"><Label>Customer</Label>
                    <Select value={importCustomer} onValueChange={setImportCustomer}>
                      <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                      <SelectContent>{tenantCustomers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="imp-csv">CSV</Label>
                  <textarea
                    id="imp-csv"
                    className="w-full h-48 rounded-md border bg-background px-3 py-2 text-sm font-mono"
                    placeholder={"reference,carrier,sku_code,qty,lot\nPO-1001,UPS,SKU-A,10,\nPO-1001,UPS,SKU-B,5,L1\nPO-1002,FEDEX,,,"}
                    value={importText}
                    onChange={(e) => setImportText(e.target.value)}
                  />
                </div>
                {importResult && (
                  <div className="text-xs space-y-1 border rounded-md p-3 bg-muted/30">
                    <div>Created: <span className="font-medium">{importResult.created}</span> · Skipped: <span className="font-medium">{importResult.skipped}</span></div>
                    {importResult.errors.length > 0 && (
                      <div className="space-y-0.5 max-h-32 overflow-auto">
                        {importResult.errors.map((er, i) => (
                          <div key={i} className="text-destructive">{er.reference}: {er.message}</div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setImportOpen(false)}>Close</Button>
                <Button onClick={onImport} disabled={importing || !importText.trim()}>
                  {importing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Import
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={(v) => { if (v && !facilityId) { handleNoSetup(); return; } setOpen(v); }}>
            <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-2" />New ASN</Button></DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>Create ASN</DialogTitle></DialogHeader>
              <form onSubmit={onSubmit} className="space-y-4">
                {!customerId && (
                  <div className="space-y-2"><Label>Customer</Label>
                    <Select value={formCustomer} onValueChange={setFormCustomer}>
                      <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                      <SelectContent>{tenantCustomers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2"><Label htmlFor="a-ref">Reference</Label><Input id="a-ref" required value={reference} onChange={(e) => setReference(e.target.value)} placeholder="PO-1001" /></div>
                <div className="space-y-2"><Label htmlFor="a-car">Carrier</Label><Input id="a-car" value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="UPS" /></div>
                <div className="space-y-2 border-t pt-4">
                  <div className="flex items-center justify-between">
                    <Label>Expected lines (optional)</Label>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setDraftLines((ls) => [...ls, { sku_id: "", qty: "", lot: "" }])}><Plus className="h-3 w-3 mr-1" />Add line</Button>
                  </div>
                  {(customerId ?? formCustomer) ? (
                    <div className="space-y-2 max-h-56 overflow-auto">
                      {draftLines.map((l, i) => (
                        <div key={i} className="flex gap-2">
                          <Select value={l.sku_id} onValueChange={(v) => setDraftLines((ls) => ls.map((x, idx) => idx === i ? { ...x, sku_id: v } : x))}>
                            <SelectTrigger className="flex-1"><SelectValue placeholder="Select SKU" /></SelectTrigger>
                            <SelectContent>{formSkus.map((s) => <SelectItem key={s.id} value={s.id}>{s.sku_code} — {s.name}</SelectItem>)}</SelectContent>
                          </Select>
                          <Input type="number" min={1} placeholder="Qty" className="w-20" value={l.qty} onChange={(e) => setDraftLines((ls) => ls.map((x, idx) => idx === i ? { ...x, qty: e.target.value } : x))} />
                          <Input placeholder="Lot" className="w-24" value={l.lot} onChange={(e) => setDraftLines((ls) => ls.map((x, idx) => idx === i ? { ...x, lot: e.target.value } : x))} />
                          <Button type="button" size="sm" variant="ghost" onClick={() => setDraftLines((ls) => ls.length > 1 ? ls.filter((_, idx) => idx !== i) : [{ sku_id: "", qty: "", lot: "" }])}>×</Button>
                        </div>
                      ))}
                    </div>
                  ) : (<p className="text-xs text-muted-foreground">Select a customer to add lines.</p>)}
                </div>
                <DialogFooter><Button type="submit" disabled={submitting}>{submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        }
      />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <ContextualTip tipKey="receiving_intro_v2" />
        <ScanInput onScan={onScan} placeholder="Scan or type ASN reference to open…" />
        <FilterBar search={search} onSearchChange={setSearch} searchPlaceholder="Search reference, carrier, customer…">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="expected">Expected</SelectItem>
              <SelectItem value="in_progress">In progress</SelectItem>
              <SelectItem value="received">Received</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <DateFieldSelect
            value={dateField}
            onChange={(v) => navigateSearch({ search: (p: typeof urlSearch) => ({ ...p, dateField: v === "created_at" ? undefined : (v as typeof urlSearch.dateField) }) })}
            options={[
              { value: "created_at", label: "Created" },
              { value: "expected_at", label: "Expected" },
            ]}
          />
          <DateRangeFilter
            value={{ start: urlSearch.start, end: urlSearch.end }}
            onChange={(r) => navigateSearch({ search: (p: typeof urlSearch) => ({ ...p, start: r.start, end: r.end }) })}
          />
          <SortDirectionToggle
            value={sort}
            onChange={(s) => navigateSearch({ search: (p: typeof urlSearch) => ({ ...p, sort: s === "desc" ? undefined : s }) })}
          />
        </FilterBar>
        <DataTable
          data={filtered}
          columns={cols}
          isLoading={isLoading}
          getRowId={(r) => r.id}
          empty={{ title: data && data.length > 0 ? "No matches" : "No ASNs yet", description: data && data.length > 0 ? "Try adjusting your search or status." : "Create an ASN to start receiving inventory." }}
          mobileCard={(a) => (
            <Link to="/app/receiving/$asnId" params={{ asnId: a.id }} className="block space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium font-mono">{a.reference}</span>
                <StatusPill tone={statusTone(a.status)} dot>{a.status}</StatusPill>
              </div>
              <div className="text-xs text-muted-foreground truncate">{customerName(a.customer_id)}{a.carrier ? ` · ${a.carrier}` : ""}</div>
              <div className="text-[11px] text-muted-foreground">{new Date(a.created_at).toLocaleDateString()}</div>
            </Link>
          )}
        />
      </div>
    </>
  );
}
